import React, {useEffect, useState} from 'react';
import {useSelector} from "react-redux";
import {useParams} from "react-router-dom";
import {Button, Col, Container, Row} from 'react-bootstrap';
import {
	ConfirmationDialog
} from "../../Common/ConfirmationDialog";
import {Glyphicon} from "../../Common/GlyphIcon";
import {StreamBankLink} from "../../Common/StreamBankLink";

import {Project} from "../../../Models/StreamBank/Project";

import {ObjectUtils} from "../../../Utils/ObjectUtils";

import {
    useChangeContext,
    useChangeContextAndTool,
} from "../../../Hooks/RouteHooks";
import {
    useEditContextNodeId,
    usePermission,
} from "../../../Hooks/PermissionHooks";
import {useMakePageRequest} from "../../../Hooks/RequestHooks";
import {useShowPermissionDenied} from "../../../Hooks/ErrorHooks";
import {useContextBuilder} from "../../../Hooks/ContextBuilderHooks";

import {useClassNames} from "../../../Hooks/StyleHooks";
import {ContextNodeBenefitSummarySection} from "../../Common/BenefitSummary/ContextNodeBenefitSummarySection";
import {ProjectModelViewer} from "./ProjectModelViewer";
import {ContactList} from "./ContactList";
import {SbMap} from "../../Common/SbMap/SbMap";
import {service as ContactService} from '../../../Services/ContactService';
import {service as ContextNodeService} from '../../../Services/ContextNodeService';
import {service as ProjectService} from '../../../Services/ProjectService';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {ContextNodeChildrenList} from "../../Common/ChildrenList/ContextNodeChildrenList";
import {useValidations} from "../../../Hooks/GenericModelHooks";
import {ValidatedFormButton} from "../../Common/ValidatedFormButton";
import {GuidUtils} from "../../../Utils";
import {AddConservationActionDropdown} from "./AddConservationActionDropdown";

let model = new Project();

export const ProjectsViewPage = () => {
    const context = useSelector(state => state.contextState.context);

    const classNames = useClassNames('sb-projects-view-page');
    const contextBuilder = useContextBuilder();
    const makePageRequest = useMakePageRequest();
    const permission = usePermission();
    const showPermissionDenied = useShowPermissionDenied();
    const editContextNodeId = useEditContextNodeId();
    const changeContext = useChangeContext();
    const changeContextAndTool = useChangeContextAndTool();

    const [project, setProject] = useState(null);
    const [existingProject, setExistingProject] = useState(null);
    const [validations, setValidations, isValid] = useValidations();
    const [relatedContacts, setRelatedContacts] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const params = useParams();

    const contextNodeId = project?.contextNodeId;
    const projectId = context.project;


    useEffect(() => {
        if (projectId) {
            loadData();
        }
    }, [projectId]);

    const loadData = () => {
        if(projectId === "new") {
            if (!permission('create_project')) {
                showPermissionDenied();
            } else {
                makePageRequest(
                    () => Promise.all([
                        ContextNodeService.Lookup({
                            programId: context.parentProgram,
                            subProgramId: context.childProgram
                        })
                    ]),
                    ([contextNodeLookup]) => {
                        const { program, subProgram } = contextNodeLookup;
                        setEditMode(true);
                        setProject({  //TODO: This seems hacky. Our code shouldn't need to know specifics of the model.
                            _tempId: GuidUtils.Generate(),
                            Location: {     // that should be defined by our model. //Maybe we can have a factory method on it?
                                datumId: 1,
                                instrumentId: 1,
                                unitId: 1
                            },
                            parentAssignedSpeciesListId: subProgram?.assignedSpeciesListId ?? program?.assignedSpeciesListId,
                            ProjectStakeholderRelationship: [],
                            programId: subProgram?.programId ?? program?.programId ?? null,
                            organizationId: program?.organizationId,
                        });
                    }
                );
            }
        } else {
            makePageRequest(
                _ => Promise.all([
                    ProjectService.GetProject(projectId),
                    ContactService.GetProjectContacts(projectId)
                ]),
                ([project, contacts]) => {
                    setProject(project?.result);
                    setRelatedContacts(contacts?.result || []);
                    setEditMode(false);
                },
            );
        }
    };

    const canEdit = () => {
        if(project) {
            if(permission('update_project')) {
                return editContextNodeId(project.contextNodeId);
            }
        }
        return false;
    };

    const canDelete = () => {
        if(project) {
            if(permission('delete_project')) {
                return editContextNodeId(project.contextNodeId);
            }
        }

        return false;
    };

    const enterEditMode = () => {
        setEditMode(true);
        setExistingProject(ObjectUtils.Clone(project));
    };

    const exitEditMode = () => {
        if(existingProject) {
            setEditMode(false);
            setProject(ObjectUtils.Clone(existingProject));
            setExistingProject(null);
        } else {
            changeContext({project: null});
        }
    };

    const updateProject = (project) => {
        setProject(project);
    };

    const saveProject = () => {
        if(project.projectId) {
            makePageRequest(
                _ => ProjectService.UpdateProject(project),
                x => {
                    setEditMode(false);
                    setProject(x.result);
                    validateSlug(x.result);

                    // TODO: update breadcrumbs from redux store
                    // instead of rebuilding context
                    contextBuilder();
                },
            );
        } else {
            makePageRequest(
                () => ProjectService.CreateProject(project),
                (x) => {
                    setProject(x.result);
                    if(x.errorMsg) {
                        setErrorMessage(x.errorMsg);
                    } else {
                        changeContextAndTool(
                            {
                                project: x.result.slug
                            },
                            "summary"
                        );
                    }
                },
            );
        }
    };

    const deleteProject = () => {
        makePageRequest(
            () => ProjectService.DeleteProject(project.projectId),
            (x) => {
                changeContext({project: null});
            },
        );
    };

    const validateSlug = (project) => {
        if(project && changeContextAndTool) {
            //if the slug doesn't match, we should update the url.
            if(
                params.pProgId !== project.grandParentSlug ||
                params.cProgId !== project.parentSlug ||
                params.projId !== project.slug
            ) {
                changeContextAndTool(
                    {
                        parentProgram: project.grandParentSlug,
                        childProgram: project.parentSlug,
                        project: project.slug,
                        contact: null
                    },
                    params.tool
                );
            }
        }
    };

    // don't render page before it is loaded
    if(project === null) {
        return null;
    }

    let projectName = project && project.name ? project.name: "Project";

    const latitude = project.Location?.latitude;
    const longitude = project.Location?.longitude;
    const accuracy = project.Location?.accuracy;

    return <Container className={classNames}>
        <Row>
            <ConfirmationDialog
                Title="Warning"
                Body={errorMessage}
                Display={!!errorMessage}
                OnConfirm={() => {
                    setErrorMessage(null);
                    changeContextAndTool(
                        {
                            project: project.slug
                        },
                        "summary"
                    );
                }}
                ConfirmationButtonMessage="OK"
            />
            <ConfirmationDialog
                Title="Confirmation"
                Body="Are you sure you want to delete this project?"
                Display={deleteDialog}
                OnClose={() => {
                    setDeleteDialog(false);
                }}
                OnConfirm={() => {
                    deleteProject();
                }}
            />
            <Col xs={12} md={6}>
                <h2 className="atomic-sectionheader">
                    <div className="float-right btn-edit">
                        {
                            editMode ? [
                                    <ValidatedFormButton
                                        variant="save"
                                        key={1}
                                        onClick={() => saveProject()}
                                        validations={validations}
                                        isValid={isValid}
                                    >
                                        Save
                                    </ValidatedFormButton>,
                                    <Button
                                        variant="cancel"
                                        key={2}
                                        onClick={() => exitEditMode()}
                                    >
                                        Cancel
                                    </Button>,
                                    projectId !== 'new' && canDelete() ?
                                        <Button
                                            variant="delete"
                                            key={3}
                                            onClick={() => setDeleteDialog(true)}
                                        >
                                            Delete
                                        </Button> :
                                        null
                                ] :
                                (
                                    canEdit() ?
                                        <Button
                                            variant="link"
                                            onClick={() => enterEditMode()}>
                                            <Glyphicon glyph="pencil">Edit</Glyphicon>
                                        </Button>:
                                        null
                                )
                        }
                    </div>
                    {projectName}
                </h2>

                {
                    !editMode && project ? [
                            <h3 key="1">Project Summary</h3>,
                            <div className="text-summary pndlm-line-break" key="2">{project.summary}</div>,
                        ] :
                        null
                }

                {
                    project ?
                        <ProjectModelViewer
                            IsNewModel={projectId === "new"}
                            Model={model}
                            EditMode={editMode}
                            Data={project}
                            Update={updateProject}
                            OnValidationUpdate={setValidations}
                            CustomData={{
                                organizationId: project.organizationId
                            }}
                        />:
                        null
                }
                <ContextNodeBenefitSummarySection contextNodeId={contextNodeId} editMode={editMode} />
            </Col>
            {
                project && projectId !== 'new' ?
            <Col xs={12} md={{span: 5, offset:1}}>
                <ContextNodeChildrenList
                    title={'Conservation Actions'}
                    showActions={canEdit()}
                    actions={<AddConservationActionDropdown />}
                    context={context}
                />
                {
                    permission('run_report_project') ?
                    <div className="sb-list-actions">
                        <h2 className="sb-children-list-title">Actions</h2>
                        <ul>
                            <li>
                                <StreamBankLink
                                    className={`link-primary`}
                                    Href={ProjectService.ProjectReportURL(project)}>
                                    <FontAwesomeIcon icon={['fas', 'file']} />
                                    <span style={{marginLeft: 6}}>Generate Report</span>
                                </StreamBankLink>
                            </li>
                        </ul>
                    </div>: null
                }
                {
                    permission('view_contacts') ?
                        <ContactList
                            projectStakeholderRelationships={project.ProjectStakeholderRelationships}
                            limit={2}
                        />:
                        null
                }
                {
                    (project.projectId && latitude && longitude) ?
                        <SbMap
                            Lat={latitude}
                            Lng={longitude}
                            Zoom={11}
                            Markers={[
                                {
                                    key: project.projectId,
                                    lat: latitude,
                                    lng: longitude,
                                    accuracy,
                                    marker: !permission('authenticated_user') ? 'circle' : 'both',
                                    content: <div>{project.name}<br />{project.description}</div>
                                }
                            ]}
                        />:
                        null
                }
            </Col> : null
            }
        </Row>
    </Container>;
};
